import { property } from '@wonderlandengine/api/decorators.js';
import { Component, Material, MeshComponent, NumberArray, Object3D } from '@wonderlandengine/api';
import { Configuration, PaintTypes } from '../data/configuration.js';
import { CloudData } from '../data/cloud-data.js';
import { PaintDescription } from './components/paint-description.js';
import { Easing, Tween } from '@tweenjs/tween.js';
import { TweenManager } from '../utilities/tween-manager.js';

//-----------------------------------------------------------------------------------------
// Type Definitions:
//-----------------------------------------------------------------------------------------

export interface CarPbrMaterial extends Material {
	albedoColor: NumberArray;
	emissiveColor: NumberArray;
	clearCoatColorF0: NumberArray;
	flakesColor: NumberArray;
	fresnelColor: NumberArray;
	metallicFactor: number;
	smoothnessFactor: number;
	occlusionFactor: number;
	flakesColorStrength: number;
	flakesScale: number;
	flakeStrength: number;
	clearCoatSmoothness: number;
	fresnelPower: number;
}

//-----------------------------------------------------------------------------------------
// Constants:
//-----------------------------------------------------------------------------------------

const COLOUR_TWEEN_TIME_IN_MS = 250;

// the paint colour whose material is used as the neutral base for a bespoke colour of each paint type, before its albedo is overridden.
// the base material is what supplies the finish (metallic factor, flakes, clear coat, fresnel), so each one is a light, neutral colour of its own type.
// only the types that offer the bespoke option appear here - satin and solitallic are fixed-colour - so this must be kept in step with
// BESPOKE_PAINT_OPTION_IDS in paintwork-view.tsx, which is what decides that. note the note under 'material assumption' in the porting
// doc before adding a layered finish: on those, a white base washes the chosen colour out and a mid-grey is wanted instead.
const BESPOKE_BASE_PAINT_IDS: Partial<Record<PaintTypes, string>> = {
	[PaintTypes.Solid]: 'PS014',															// gloss solid classic white.
	[PaintTypes.Metallic]: 'PM002'															// metallic silver.
};

/**
 * Paint Configurator:
 */
export class PaintConfigurator extends Component {
	static TypeName = 'paint-configurator';

	//-----------------------------------------------------------------------------------------
	// Inspector Variables:
	//-----------------------------------------------------------------------------------------

	@property.object({ required: true })
	paintDescriptions!: Object3D;

	@property.object({ required: true })
	frontWing!: Object3D;

	@property.object({ required: true })
	frontWing400!: Object3D;

	@property.object({ required: true })
	nosePanel!: Object3D;

	@property.object({ required: true })
	bonnet!: Object3D;
	
	@property.object({ required: true })
	boot!: Object3D;

	@property.object({ required: true })
	rearWing!: Object3D;

	@property.object({ required: true })
	doors!: Object3D;

	@property.object({ required: true })
	mainPanel!: Object3D;

	@property.object({ required: true })
	windscreen!: Object3D;

	@property.object({ required: true })
	seatBackCarPaint!: Object3D;
	
	@property.object({ required: true })
	sideCarPaint!: Object3D;

	@property.object({ required: true })
	hardtopExterior!: Object3D;

	//-----------------------------------------------------------------------------------------
	// Fields:
	//-----------------------------------------------------------------------------------------

	// component references.
	private _paintDescriptions!: PaintDescription[];
	private _frontWing!: MeshComponent;
	private _frontWing400!: MeshComponent;
	private _nosePanel!: MeshComponent;
	private _bonnet!: MeshComponent;
	private _boot!: MeshComponent;
	private _rearWing!: MeshComponent;
	private _doors!: MeshComponent;
	private _mainPanel!: MeshComponent;
	private _windscreen!: MeshComponent;
	private _seatBackCarPaint!: MeshComponent;
	private _sideCarPaint!: MeshComponent;
	private _hardtopExterior!: MeshComponent;

	private _tweenManager = new TweenManager();

	// the per-mesh materials used while a bespoke colour is applied, cached so that dragging the picker is a cheap albedo write rather than a fresh clone/tween each time. null when no bespoke colour is active.
	private _bespokeMaterials: CarPbrMaterial[] | null = null;

	// the paint type the cached bespoke materials were cloned from, so that basing the bespoke colour on another type re-clones from that type's base material. null when no bespoke colour is active.
	private _bespokePaintType: PaintTypes | null = null;

	// the meshes those materials were cloned onto. the body mesh set is not fixed - the hardtop exterior leaves it when the hardtop
	// is set to grey and rejoins it when it is not - and a mesh that joins after the clones were made would otherwise be left out of
	// the albedo writes, frozen on the colour it had when it joined while the rest of the body followed the picker.
	private _bespokeMeshes: MeshComponent[] | null = null;

	//-----------------------------------------------------------------------------------------
	// Lifecycle Functions:
	//-----------------------------------------------------------------------------------------

	start(): void {

		// get components.
		this._paintDescriptions = this.paintDescriptions.getComponents(PaintDescription)!;
		this._frontWing = this.frontWing.getComponent(MeshComponent)!;
		this._frontWing400 = this.frontWing400.getComponent(MeshComponent)!;
		this._nosePanel = this.nosePanel.getComponent(MeshComponent)!;
		this._bonnet = this.bonnet.getComponent(MeshComponent)!;
		this._boot = this.boot.getComponent(MeshComponent)!;
		this._rearWing = this.rearWing.getComponent(MeshComponent)!;
		this._doors = this.doors.getComponent(MeshComponent)!;
		this._mainPanel = this.mainPanel.getComponent(MeshComponent)!;
		this._windscreen = this.windscreen.getComponent(MeshComponent)!;
		this._seatBackCarPaint = this.seatBackCarPaint.getComponent(MeshComponent)!;
		this._sideCarPaint = this.seatBackCarPaint.getComponent(MeshComponent)!;
		this._hardtopExterior = this.hardtopExterior.getComponent(MeshComponent)!;
	}

	update() {
		this._tweenManager.update();
	}

	//-----------------------------------------------------------------------------------------
	// Public Functions:
	//-----------------------------------------------------------------------------------------

	/**
	 * Selects the body paint colour with the given ID, updating the relevant meshes/materials on the vehicle model.
	 */
	selectBodyPaint(id: string) {

		// a bespoke option id stands for 'a colour of the customer's own', not for a paint description, so there is nothing to look up
		// and any colour already applied should stay. bespoke colours arrive via selectBespokeBodyPaint() instead.
		if (Configuration.isBespokePaintColour(id)) {
			return;
		}

		// selecting a standard paint clears any active bespoke colour, so re-opening the picker starts from the base material again.
		this._bespokeMaterials = null;
		this._bespokePaintType = null;
		this._bespokeMeshes = null;

		const material = this._getMaterialFromId(id);
		if (material) {

			// fade each body mesh to the selected paint material.
			for (const mesh of this._getBodyMeshes()) {
				this._fadeColour(mesh, material);
			}
		}
	}

	/**
	 * Applies a bespoke (arbitrary) body colour, given as an rgba array of 0–1 floats, updating the vehicle model in real time.
	 * The colour is applied over the neutral base material of the given paint type, so that the finish (metallic, satin, solitallic or plain solid)
	 * matches the panel the colour was chosen in. The first call for a paint type clones that base material onto each body mesh; subsequent calls
	 * just overwrite the albedo, so dragging the picker stays cheap.
	 */
	selectBespokeBodyPaint(colour: NumberArray, paintType: PaintTypes) {

		// on the first application, whenever the colour is re-based onto another paint type, and whenever the body mesh set itself has
		// changed, clone that type's base material onto each body mesh and cache the clones.
		const bodyMeshes = this._getBodyMeshes();
		if (!this._bespokeMaterials || this._bespokePaintType !== paintType || !isSameMeshSet(this._bespokeMeshes, bodyMeshes)) {

			const basePaintId = getBespokeBasePaintId(paintType);
			const baseMaterial = basePaintId ? this._getMaterialFromId(basePaintId) : null;
			if (!baseMaterial) {
				return;
			}

			this._bespokeMaterials = [];
			for (const mesh of bodyMeshes) {
				const material = baseMaterial.clone() as CarPbrMaterial;
				mesh.material = material;
				this._bespokeMaterials.push(material);
			}

			// remember the type and the meshes the clones came from, so a later call matching both reuses them.
			this._bespokePaintType = paintType;
			this._bespokeMeshes = bodyMeshes;
		}

		// overwrite the albedo of each cached material with the chosen colour.
		for (const material of this._bespokeMaterials) {
			material.albedoColor = [colour[0], colour[1], colour[2], colour[3] ?? 1];
		}
	}

	//-----------------------------------------------------------------------------------------
	// Private Functions:
	//-----------------------------------------------------------------------------------------

	// returns the body meshes that make up the painted exterior, including the hardtop unless it is set to grey (Supersport 400 only).
	private _getBodyMeshes(): MeshComponent[] {

		const meshes = [
			this._frontWing,
			this._frontWing400,
			this._nosePanel,
			this._bonnet,
			this._boot,
			this._rearWing,
			this._doors,
			this._mainPanel,
			this._windscreen,
			this._seatBackCarPaint,
			this._sideCarPaint
		];

		// only include the hardtop exterior if the hardtop option is not set to grey (Supersport 400 only).
		if (!Configuration.isGreyHardTop(Configuration.hardTopOption)) {
			meshes.push(this._hardtopExterior);
		}

		return meshes;
	}

	private _getMaterialFromId(id: string): CarPbrMaterial | null {

		// get the paint description for the given ID.
		const paintDescription = this._paintDescriptions.find(element => element.paintId === id);
		if (!paintDescription) {
			console.error('No paint description found for id: ', id);
			return null;
		}

		// get the relevant material from the paint description.
		const material = paintDescription.paintMaterial as CarPbrMaterial;
		if (!material) {
			console.error('No paint material assigned for id: ', id);
			return null;
		}

		// return the material.
		return material;
	}

	/**
	 * Smoothly fades the colour of the material on the given mesh between the current colour and the colour defined by the given material,
	 * using a tween animation to change the relevant material properties.
	 */
	private _fadeColour(mesh: MeshComponent, targetMaterial: CarPbrMaterial) {

		// ensure the given mesh has a material assigned to it.
		if (!mesh.material) {
			console.error('No material has been assigned to the mesh: ', mesh);
			return;
		}

		// clone the current material (as we don't want to change the source material itself).
		const material = mesh.material.clone() as CarPbrMaterial;
		mesh.material = material;

		// tween the relevant material property to change the colour.
		const colourTween = new Tween({
			albedoColor: material.albedoColor,
			emissiveColor: material.emissiveColor,
			clearCoatColorF0: material.clearCoatColorF0,
			flakesColor: material.flakesColor,
			fresnelColor: material.fresnelColor,
			metallicFactor: material.metallicFactor,
			smoothnessFactor: material.smoothnessFactor,
			occlusionFactor: material.occlusionFactor,
			flakesColorStrength: material.flakesColorStrength,
			flakesScale: material.flakesScale,
			flakeStrength: material.flakeStrength,
			clearCoatSmoothness: material.clearCoatSmoothness,
			fresnelPower: material.fresnelPower
		});
		colourTween
			.to(
				{
					albedoColor: targetMaterial.albedoColor,
					emissiveColor: targetMaterial.emissiveColor,
					clearCoatColorF0: targetMaterial.clearCoatColorF0,
					flakesColor: targetMaterial.flakesColor,
					fresnelColor: targetMaterial.fresnelColor,
					metallicFactor: targetMaterial.metallicFactor,
					smoothnessFactor: targetMaterial.smoothnessFactor,
					occlusionFactor: targetMaterial.occlusionFactor,
					flakesColorStrength: targetMaterial.flakesColorStrength,
					flakesScale: targetMaterial.flakesScale,
					flakeStrength: targetMaterial.flakeStrength,
					clearCoatSmoothness: targetMaterial.clearCoatSmoothness,
					fresnelPower: targetMaterial.fresnelPower
				},
				COLOUR_TWEEN_TIME_IN_MS
			)
			.onUpdate((c) => {
				material.albedoColor = c.albedoColor;
				material.emissiveColor = c.emissiveColor;
				material.clearCoatColorF0 = c.clearCoatColorF0;
				material.flakesColor = c.flakesColor;
				material.fresnelColor = c.fresnelColor;
				material.metallicFactor = c.metallicFactor;
				material.smoothnessFactor = c.smoothnessFactor;
				material.occlusionFactor = c.occlusionFactor;
				material.flakesColorStrength = c.flakesColorStrength;
				material.flakesScale = c.flakesScale;
				material.flakeStrength = c.flakeStrength;
				material.clearCoatSmoothness = c.clearCoatSmoothness;
				material.fresnelPower = c.fresnelPower;
			})
			.easing(Easing.Quadratic.InOut);
		this._tweenManager.start(colourTween);
	}
}

//-----------------------------------------------------------------------------------------
// Public Functions:
//-----------------------------------------------------------------------------------------

/**
 * Returns a material representing the currently selected body colour, for the other configurators to put on the
 * body-coloured parts they own (the hard top exterior, a body-matching dashboard face). Always a fresh clone, so the
 * caller may modify it - setting a matte smoothness, say - without disturbing the shared paint description material.
 * Returns null if no material can be resolved, having logged why.
 */
export function cloneBodyPaintMaterial(paintDescriptions: PaintDescription[]): CarPbrMaterial | null {

	const id = Configuration.bodyPaintColour;

	// a bespoke colour has no paint description of its own, so its finish is taken from its paint type's base paint
	// and its colour from the configuration - the same pair selectBespokeBodyPaint() applies to the body itself.
	const isBespokeColour = Configuration.isBespokePaintColour(id);
	const materialId = isBespokeColour ? getBespokeBasePaintId(CloudData.getPaintTypeForId(id)) : id;
	if (!materialId) {
		return null;
	}

	// get the paint description supplying the material.
	const paintDescription = paintDescriptions.find(element => element.paintId === materialId);
	if (!paintDescription) {
		console.error('No paint description found for id: ', materialId);
		return null;
	}

	// clone it, so that the caller owns the material it is given.
	const material = paintDescription.paintMaterial?.clone() as CarPbrMaterial;
	if (!material) {
		console.error('No paint material assigned for id: ', materialId);
		return null;
	}

	// for a bespoke colour, overwrite the base material's albedo with the chosen colour, leaving the rest of its finish alone.
	if (isBespokeColour && Configuration.bespokePaintColour) {
		const colour = Configuration.bespokePaintColour;
		material.albedoColor = [colour[0], colour[1], colour[2], colour[3] ?? 1];
	}

	return material;
}

//-----------------------------------------------------------------------------------------
// Private Functions:
//-----------------------------------------------------------------------------------------

// resolves the id of the neutral base paint a bespoke colour of the given paint type is applied over. a paint type absent from the
// map does not offer the bespoke option at all, so nothing should ever ask for one - hence the log rather than a silent null.
function getBespokeBasePaintId(paintType: PaintTypes): string | null {

	const basePaintId = BESPOKE_BASE_PAINT_IDS[paintType];
	if (!basePaintId) {
		console.error('No bespoke base paint defined for paint type: ', PaintTypes[paintType]);
		return null;
	}

	return basePaintId;
}

// compares two body mesh sets. identity and order are enough: _getBodyMeshes() builds its list the same way every time, so the only
// thing that varies between two calls is whether the conditional mesh at the end of it is present.
function isSameMeshSet(first: MeshComponent[] | null, second: MeshComponent[]): boolean {

	if (!first || first.length !== second.length) {
		return false;
	}

	return first.every((mesh, index) => mesh === second[index]);
}
